import { create } from "zustand";

import type { AuthUserJWT, User } from "@/types/auth";

import { useAuth } from "@/contexts/AuthContext";
import { canAccessBesties } from "@/features/BestiesOnboarding/access";
import { isElevatedRole } from "@/utils/roleAccess";

const STORAGE_KEY = "connect-ui:privileged-mode-disabled";

export const isPrivilegedModeToggleAvailable = import.meta.env.DEV;

export function isPrivilegedDevToolsVisible(
  isRealRoleElevated: boolean,
): boolean {
  return isPrivilegedModeToggleAvailable && isRealRoleElevated;
}

const readInitial = (): boolean => {
  if (!isPrivilegedModeToggleAvailable) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

interface PrivilegedModeState {
  disabled: boolean;
  setDisabled: (value: boolean) => void;
}

export const usePrivilegedModeStore = create<PrivilegedModeState>((set) => ({
  disabled: readInitial(),
  setDisabled: (value) => {
    if (isPrivilegedModeToggleAvailable) {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(value));
      } catch {
        /* ignore quota / disabled storage */
      }
    }
    set({ disabled: value });
  },
}));

function applyOverride(realValue: boolean, disabled: boolean): boolean {
  if (!isPrivilegedModeToggleAvailable) return realValue;
  return realValue && !disabled;
}

// Non-hook elevated-access check for use outside React (e.g. a route's
// beforeLoad), so guards see the same role + privileged-toggle result the UI
// does. Pair with usePrivilegedModeStore.getState().disabled.
export function hasElevatedAccessValue(
  role: User["role"] | null | undefined,
  disabled: boolean,
): boolean {
  return applyOverride(isElevatedRole(role), disabled);
}

export function useHasElevatedAccess(): boolean {
  const { user } = useAuth();
  const disabled = usePrivilegedModeStore((s) => s.disabled);
  return hasElevatedAccessValue(user?.user?.role, disabled);
}

export function useIsRealRoleElevated(): boolean {
  const { user } = useAuth();
  return isElevatedRole(user?.user?.role);
}

export function useCanAccessBesties(
  role: User["role"] | null | undefined,
  orgType: AuthUserJWT["org_type"] | undefined,
): boolean {
  const disabled = usePrivilegedModeStore((s) => s.disabled);
  if (!isPrivilegedModeToggleAvailable || !disabled) {
    return canAccessBesties(role, orgType);
  }
  return orgType === "agency";
}
